import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Upload, Camera, X, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import { TopBar } from '../components/TopBar';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { useAuth } from '../contexts/AuthContext';


export const UploadImagesScreen = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [price, setPrice] = useState('');
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    
    if (selectedFiles.length + files.length > 15) {
      toast.error('Maximum 15 images allowed');
      return;
    }

    // Create previews
    const newPreviews = files.map(file => URL.createObjectURL(file));
    setPreviews([...previews, ...newPreviews]);
    setSelectedFiles([...selectedFiles, ...files]);
  };

  const removeImage = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previews];
    
    URL.revokeObjectURL(newPreviews[index]);
    
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    
    setSelectedFiles(newFiles);
    setPreviews(newPreviews);
  };

  const handleGenerate = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please upload at least one image');
      return;
    }

    setUploading(true);

    try {
      // Upload images
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });

      const uploadRes = await api.post(
        `/products/upload-images`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          timeout: 60000
        }
      );

      const productId = uploadRes.data.product_id;

      // Update price if provided
      if (price) {
        await api.put(
          `/products/${productId}`,
          { price: parseFloat(price) }
        );
      }

      setUploading(false);
      setGenerating(true);

      // Generate AI listing (AI processing can take up to 60s)
      const genRes = await api.post(
        `/products/generate-listing`,
        { product_id: productId },
        { timeout: 120000 }
      );

      if (genRes.data.success) {
        toast.success('Listing created! Open Content Studio for full marketing kit.');
        navigate(`/content-studio/${productId}`);
      }
    } catch (error) {
      console.error('Error:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to create listing: ${msg}`);
      setUploading(false);
      setGenerating(false);
    }
  };

  if (uploading) {
    return <LoadingOverlay message="Uploading images..." />;
  }

  if (generating) {
    return <LoadingOverlay message="AI is creating your listing..." />;
  }

  return (
    <div className="min-h-screen bg-[#F0F2F5]">
      <div className="w-full max-w-md mx-auto bg-white min-h-screen shadow-xl">
        <TopBar title="Sell Product" showBack={true} />

        <div className="p-6 space-y-6">
          {/* Instruction Card */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <p className="text-base text-blue-900">
              <strong>Quick Tip:</strong> Take clear photos with good lighting for best results!
            </p>
          </div>

          {/* Upload Area */}
          {previews.length === 0 ? (
            <label
              htmlFor="file-upload"
              className="w-full min-h-[300px] bg-gray-50 border-2 border-dashed border-[#128C7E] rounded-2xl flex flex-col items-center justify-center gap-4 text-[#128C7E] active:bg-green-50 transition-colors cursor-pointer p-6"
              data-testid="upload-area"
            >
              <Camera size={64} strokeWidth={1.5} />
              <div className="text-center">
                <p className="font-bold text-xl mb-2">Upload Photos</p>
                <p className="text-base text-[#667781]">Tap to add 1-15 images</p>
                <p className="text-sm text-[#667781] mt-2">PNG, JPG, WEBP supported</p>
              </div>
              <input
                id="file-upload"
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                capture="environment"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </label>
          ) : (
            <div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {previews.map((preview, index) => (
                  <div key={index} className="relative aspect-square rounded-xl overflow-hidden bg-gray-100 border-2 border-gray-200">
                    <img
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removeImage(index)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 shadow-lg active:scale-95 transition-transform"
                      data-testid={`remove-image-${index}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                {previews.length < 15 && (
                  <label
                    htmlFor="file-upload-more"
                    className="aspect-square rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer active:bg-gray-50 transition-colors"
                    data-testid="add-more-button"
                  >
                    <Upload size={28} className="text-gray-400" />
                    <input
                      id="file-upload-more"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,image/webp"
                      capture="environment"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <p className="text-base text-[#667781] text-center font-medium">
                {previews.length} of 15 images
              </p>
            </div>
          )}

          {/* Price Input (Optional) */}
          <div>
            <label className="block text-base font-semibold text-slate-800 mb-3">
              Price (Optional)
            </label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl font-bold text-slate-600">
                ₹
              </span>
              <input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0"
                className="w-full h-16 pl-10 pr-4 bg-gray-50 border-2 border-gray-200 rounded-xl text-xl focus:ring-2 focus:ring-[#128C7E] focus:border-transparent outline-none transition-all"
                data-testid="price-input"
              />
            </div>
            <p className="text-sm text-[#667781] mt-2">
              You can add or change price later
            </p>
          </div>

          {/* Generate Button */}
          {selectedFiles.length > 0 && (
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={handleGenerate}
              className="w-full h-16 bg-[#128C7E] text-white rounded-xl text-xl font-bold flex items-center justify-center gap-3 transition-transform active:scale-95 shadow-lg"
              data-testid="generate-listing-button"
            >
              <Sparkles size={24} />
              Generate Listing with AI
            </motion.button>
          )}
        </div>
      </div>
    </div>
  );
};
